window.defaultBps = {
  desktop: 1023,
  tablet: 836,
  mobile: 414,
  above: {
    sm: window.matchMedia("(min-width: 414px)"),
    md: window.matchMedia("(min-width: 836px)"),
    lg: window.matchMedia("(min-width: 1024px)"),
  },
  below: {
    sm: window.matchMedia("(max-width: 413px)"),
    md: window.matchMedia("(max-width: 835px)"),
    lg: window.matchMedia("(max-width: 1023px)"),
  },
};

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container, lastElementToFocus) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = lastElementToFocus ?? elements[elements.length - 1];
  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (
      event.target !== container &&
      event.target !== last &&
      event.target !== first
    )
      return;

    document.addEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== "TAB") return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if (
      (event.target === container || event.target === first) &&
      event.shiftKey
    ) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener("focusout", trapFocusHandlers.focusout);
  document.addEventListener("focusin", trapFocusHandlers.focusin);

  if (elementToFocus) {
    elementToFocus?.focus();

    if (
      elementToFocus.tagName === "INPUT" &&
      ["search", "text", "email", "url"].includes(elementToFocus.type) &&
      elementToFocus.value
    ) {
      elementToFocus.setSelectionRange(0, elementToFocus.value.length);
    }
  }
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener("focusin", trapFocusHandlers.focusin);
  document.removeEventListener("focusout", trapFocusHandlers.focusout);
  document.removeEventListener("keydown", trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function pauseAllMedia() {
  document.querySelectorAll(".js-youtube").forEach((video) => {
    video.contentWindow.postMessage(
      '{"event":"command","func":"' + "pauseVideo" + '","args":""}',
      "*"
    );
  });
  document.querySelectorAll(".js-vimeo").forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', "*");
  });
  document.querySelectorAll("video").forEach((video) => {
    video.pause();
    video.closest("deferred-media").pauseVideo();
  });
}

function initSplideWithAutoWidth(
  selector,
  desktopOptions,
  mobileOptions,
  width = false
) {
  const elements = document.querySelectorAll(selector);
  const currentSliders = [];

  init();

  function init() {
    if (!elements.length) return;
    addEvents();
  }

  function swapSliders() {
    destroySliders();
    if (window.innerWidth > 414) {
      removeSlidesWidth();
      initSliders(desktopOptions);
    } else {
      initSliders(mobileOptions);
      updateSlidesWidth();
    }
  }

  function initSliders(options) {
    elements.forEach((slider) => {
      currentSliders.push(new window.app.Splide(slider, options).mount());
    });
  }

  function destroySliders() {
    if (!currentSliders.length) return;
    currentSliders.forEach((slider) => slider.destroy());
    currentSliders.length = 0;
  }

  function updateSlidesWidth() {
    if (!width) return;
    elements.forEach((el) => {
      const slides = $(el).find(".splide__slide");
      slides.css("width", width);
    });
  }

  function removeSlidesWidth() {
    elements.forEach((el) => {
      const slides = $(el).find(".splide__slide");
      slides.removeAttr("style");
    });
  }

  function addEvents() {
    window.addEventListener("resize", debounce(swapSliders, 500));
    window.dispatchEvent(new Event("resize"));
  }
}

document.addEventListener("DOMContentLoaded", () => {
  document.addEventListener("openModal", (event) => {
    if (!event.detail.modal) return;
    const modal = document.querySelector(event.detail.modal);
    if (modal) modal.show();
  });
});

class ModalDialog extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.closeOnBackdropClick = this.hasAttribute("close-on-backdrop-click");
    this.querySelector('[role="dialog"]')?.addEventListener(
      "click",
      (event) => {
        this._handleBackdropClick(event);
      }
    );

    const closeButton = this.querySelector('[id^="ModalClose-"]');
    if (closeButton) {
      this._addTouchAndClickEvents(closeButton);
    }

    this.querySelectorAll("[data-modal-close], [data-dialog-close], [data-fancybox-close]").forEach(
      (el) => {
        this._addTouchAndClickEvents(el);
      }
    );

    this.addEventListener("keyup", (event) => {
      if (event.code.toUpperCase() === "ESCAPE") this.hide();
    });

    if (this.classList.contains("media-modal")) {
      this.addEventListener("pointerup", (event) => {
        if (
          event.pointerType === "mouse" &&
          !event.target.closest("deferred-media, product-model")
        )
          this.hide();
      });
    } else {
      this.addEventListener("click", (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  show(opener = null) {
    this.openedBy = opener;
    const popup = this.querySelector(".template-popup");
    document.documentElement.classList.add("overflow-hidden");
    document.body.classList.add("overflow-hidden");
    this.querySelector('[role="dialog"]')?.showModal();
    this.open = true;
    if (popup) popup.loadContent();
    if (opener) this._focusOnFirstElement();
    window.pauseAllMedia();
  }

  hide() {
    document.documentElement.classList.remove("overflow-hidden");
    document.body.classList.remove("overflow-hidden");
    document.body.dispatchEvent(new CustomEvent("modalClosed"));
    this.querySelector('[role="dialog"]')?.close();
    this.open = false;
    if (this.openedBy) {
      this.openedBy.focus();
    }
    window.pauseAllMedia();
  }

  _focusOnFirstElement() {
    try {
      setTimeout(async () => {
        const dialog = await this.querySelector('[role="dialog"]');
        if (dialog) {
          const firstFocusableElement = dialog.querySelector(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
          );

          if (firstFocusableElement) {
            firstFocusableElement.focus();
          }
        }
      }, 300);
    } catch (error) {
      console.error("Error in _focusOnFirstElement", error);
    }
  }

  /**
   * Adds both touch and click event listeners to an element to ensure consistent behavior across devices
   * @param {HTMLElement} element - The element to attach events to
   * @private
   */
  _addTouchAndClickEvents(element) {
    // Using the click event with a passive flag for better touch handling
    element.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.hide();
    }, { passive: false });
  }

  _handleBackdropClick(event) {
    if (!this.closeOnBackdropClick) {
      return;
    }

    if (
      event.target instanceof HTMLElement &&
      event.target.tagName === "DIALOG"
    ) {
      this.hide();
    }
  }
}
customElements.define("modal-dialog", ModalDialog);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector("button");

    if (!button) return;
    button.addEventListener("click", () => {
      const modal = document.querySelector(this.getAttribute("data-modal"));
      if (modal) modal.show(button);
    });
  }
}
customElements.define("dialog-toggle", ModalOpener);
